import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import ArticleCard from "@/components/feature/ArticleCard";
import { articles } from "@/mocks/articles";

function formatCount(n: number) {
  if (n >= 10000) return `${(n / 10000).toFixed(1)}万`;
  return n.toLocaleString("zh-CN");
}

export default function ArticleDetail() {
  const { slug = "" } = useParams();
  const article = articles.find((a) => a.slug === slug);
  const index = article ? articles.findIndex((a) => a.slug === slug) : -1;

  useEffect(() => {
    if (!article) return;
    const prevTag = document.getElementById("ld-article");
    if (prevTag) prevTag.remove();
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "ld-article";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.excerpt,
      datePublished: article.date,
      author: { "@type": "Person", name: article.author },
      publisher: { "@type": "Organization", name: "师兄啊师兄讨论区" },
      keywords: article.tags.join(","),
      mainEntityOfPage: `https://cultivate3.github.io/article/${article.slug}`,
    });
    document.head.appendChild(script);
    return () => {
      document.getElementById("ld-article")?.remove();
    };
  }, [article]);

  if (!article) {
    return (
      <div className="container-x flex min-h-[60vh] flex-col items-center justify-center text-center py-16">
        <h1 className="font-heading text-2xl font-bold text-foreground-950">未找到这篇文章</h1>
        <p className="mt-2 text-sm text-foreground-500">内容可能已下架或链接有误。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
        >
          返回首页
          <i className="ri-arrow-right-line"></i>
        </Link>
      </div>
    );
  }

  const related = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .concat(articles.filter((a) => a.category !== article.category))
    .slice(0, 3);
  const prev = index > 0 ? articles[index - 1] : null;
  const next = index >= 0 && index < articles.length - 1 ? articles[index + 1] : null;

  return (
    <>
      <Seo
        title={`${article.title} · 师兄啊师兄剧情解析`}
        description={article.excerpt}
        keywords={article.tags.join(",")}
        type="article"
        path={`/article/${article.slug}`}
      />

      <article className="container-x max-w-4xl py-10 md:py-14">
        <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-400" aria-label="面包屑">
          <Link to="/" className="hover:text-primary-600">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to={`/section/${article.category}`} className="hover:text-primary-600">
            {article.categoryName}
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="line-clamp-1 text-foreground-600">{article.title}</span>
        </nav>

        <header className="mt-6">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
            {article.categoryName}
          </span>
          <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
            {article.title}
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-foreground-600">{article.excerpt}</p>

          <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 border-y border-background-200 py-3.5 text-xs text-foreground-400">
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              <span className="h-7 w-7 items-center justify-center overflow-hidden rounded-full bg-primary-100 text-primary-600 hidden sm:flex">
                <i className="ri-user-3-line text-sm"></i>
              </span>
              {article.author}
            </span>
            <span className="flex items-center gap-1 whitespace-nowrap">
              <i className="ri-calendar-line"></i>
              {article.date}
            </span>
            <span className="flex items-center gap-1 whitespace-nowrap">
              <i className="ri-eye-line"></i>
              阅读 {formatCount(article.reads)}
            </span>
            <span className="flex items-center gap-1 whitespace-nowrap">
              <i className="ri-chat-1-line"></i>
              讨论 {formatCount(article.comments)}
            </span>
          </div>
        </header>

        <figure className="mt-6 overflow-hidden rounded-xl border border-background-200/70">
          <div className="aspect-[16/9] w-full">
            <img src={article.cover} alt={`${article.title} 封面配图`} className="h-full w-full object-cover" />
          </div>
        </figure>

        <div className="mt-8 space-y-6">
          {article.content.map((para, i) => (
            <p key={i} className="text-[15px] leading-8 text-foreground-700 md:text-base md:leading-9">
              {para}
            </p>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
          <span className="text-sm text-foreground-400">标签：</span>
          {article.tags.map((t) => (
            <Link
              key={t}
              to={`/section/${article.category}`}
              className="rounded-full border border-background-200 bg-background-100/70 px-3 py-1 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              #{t}
            </Link>
          ))}
        </div>

        <aside className="mt-8 flex items-center gap-4 rounded-xl border border-background-200/70 bg-background-100/60 p-5" aria-label="作者">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-600">
            <i className="ri-user-3-line text-xl"></i>
          </span>
          <div className="flex-1">
            <p className="text-sm font-medium text-foreground-900">作者 · {article.author}</p>
            <p className="mt-1 text-xs leading-relaxed text-foreground-500">
              资深追更党，主攻宗门篇剧情梳理与伏笔挖掘，喜欢把故事讲清楚。
            </p>
          </div>
          <Link
            to="/contact"
            className="hidden items-center gap-1 whitespace-nowrap rounded-full border border-primary-300 px-4 py-2 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-50 sm:inline-flex"
          >
            <i className="ri-chat-3-line"></i>
            聊聊观点
          </Link>
        </aside>

        <nav className="mt-8 flex flex-col gap-3 sm:flex-row" aria-label="上一篇下一篇">
          {prev ? (
            <Link
              to={`/article/${prev.slug}`}
              className="flex-1 rounded-xl border border-background-200/70 bg-background-50 p-4 transition-colors hover:border-primary-200"
            >
              <span className="text-xs text-foreground-400">上一篇</span>
              <p className="mt-1 line-clamp-1 text-sm font-medium text-foreground-900 hover:text-primary-700">{prev.title}</p>
            </Link>
          ) : (
            <div className="flex-1 rounded-xl border border-dashed border-background-200 p-4">
              <span className="text-xs text-foreground-400">上一篇</span>
              <p className="mt-1 text-sm text-foreground-400">已经是第一篇了</p>
            </div>
          )}
          {next ? (
            <Link
              to={`/article/${next.slug}`}
              className="flex-1 rounded-xl border border-background-200/70 bg-background-50 p-4 text-right transition-colors hover:border-primary-200"
            >
              <span className="text-xs text-foreground-400">下一篇</span>
              <p className="mt-1 line-clamp-1 text-sm font-medium text-foreground-900 hover:text-primary-700">{next.title}</p>
            </Link>
          ) : (
            <div className="flex-1 rounded-xl border border-dashed border-background-200 p-4 text-right">
              <span className="text-xs text-foreground-400">下一篇</span>
              <p className="mt-1 text-sm text-foreground-400">已经是最新一篇</p>
            </div>
          )}
        </nav>
      </article>

      <section className="border-t border-background-200 bg-background-100/60 py-10 md:py-14">
        <div className="container-x">
          <div className="flex items-end justify-between gap-4">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">相关阅读推荐</h2>
            <Link
              to="/section/all"
              className="hidden items-center gap-1 whitespace-nowrap text-sm text-primary-600 hover:text-primary-700 sm:flex"
            >
              全部讨论
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((a) => (
              <ArticleCard key={a.id} article={a} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}